import { Caption } from '@components/utils/text';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { PriceFragment } from '@graphql/generated/graphql';
import { getFormattedPrice } from '@utils/price';
import { useRouter } from 'next/router';

interface Props {
    price?: PriceFragment;
    discountedPrice?: PriceFragment;
    color?: string;
    size?: 'sm' | 'md' | 'lg';
}

const Wrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.25rem;
`;

const PriceText = styled(Caption)<{
    $color?: string;
    $size?: 'sm' | 'md' | 'lg';
}>`
    ${({ $color }) => $color && `color: ${$color};`}
    ${({ theme, $size }) => {
        if ($size === 'lg')
            return css`
                font: ${theme.typography.heading3};
                font-weight: 500;
            `;
        if ($size === 'md')
            return css`
                font: ${theme.fonts.subtitle5};
                font-weight: 500;
            `;
        return css`
            font: ${theme.fonts.subtitle7};
            font-weight: lighter;
        `;
    }}
`;

const StrikethroughPriceText = styled(PriceText)`
    text-decoration: line-through;
`;

export const Price = ({ price, discountedPrice, color, size }: Props) => {
    const { locale } = useRouter();

    if (!price && !discountedPrice) return null;

    if (price && discountedPrice) {
        return (
            <Wrapper>
                <StrikethroughPriceText $color={color} $size={size}>
                    {getFormattedPrice(price, locale!)}
                </StrikethroughPriceText>
                <PriceText $color={color} $size={size}>
                    {getFormattedPrice(discountedPrice, locale!)}
                </PriceText>
            </Wrapper>
        );
    }

    return (
        <Wrapper>
            <PriceText $color={color} $size={size}>
                {getFormattedPrice((price ?? discountedPrice)!, locale!)}
            </PriceText>
        </Wrapper>
    );
};
